import React, { useContext, useState, useEffect } from "react";
import { Modal, TextInput, Label, Select } from "flowbite-react";
import Swal from "sweetalert2";
import axios from "axios";
import DataTable from "react-data-table-component";
import engineGif from "../image/engine.gif";
import Button_submit from "../components/Button_submit";
import GlobalContext from "../connections/GlobalContext";
import Head_data from "../components/Head_data";
import jsPDF from "jspdf";
import Logo from "../image/logo.jpg";
import html2canvas from "html2canvas";

const Expenses = ({ theme }) => {
  const { globalValue } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("add");

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    let mm = today.getMonth() + 1; // Months start at 0
    let dd = today.getDate();

    // Add leading zeros if needed
    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;

    return `${yyyy}-${mm}-${dd}`;
  };

  const [formData, setFormData] = useState({
    id: null,
    category_id: "",
    amount: "",
    date: getTodayDate(),
    staffs: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredExpenses, setFilteredExpenses] = useState([]);

  const fetchExpenses = () => {
    setLoading(true);
    const branch = sessionStorage.getItem("branch");

    axios
      .get(`${globalValue}/expense_display`, {
        params: { branch },
      })
      .then((res) => {
        setExpenses(res.data);
        setFilteredExpenses(res.data);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  const fetchCategories = () => {
    setLoading(true);
    axios
      .get(`${globalValue}/category_display`)
      .then((res) => setCategories(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
  }, []);

  useEffect(() => {
    const filtered = expenses.filter(
      (row) =>
        (row.category_name &&
          row.category_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.staffs &&
          row.staffs.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredExpenses(filtered);
  }, [searchTerm, expenses]);

  const handleSubmit = async () => {
    if (
      !formData.category_id ||
      !formData.amount ||
      !formData.date ||
      !formData.staffs
    ) {
      Swal.fire("Error", "All fields are required", "error");
      return;
    }

    const url = formData.id
      ? `${globalValue}/expense_edit`
      : `${globalValue}/expense_add`;

    setLoading(true);

    try {
      const payload = {
        ...formData,
        branch: sessionStorage.getItem("branch"),
      };

      const res = await axios.post(url, payload);

      if (res.data.success === 1) {
        Swal.fire("Success", res.data.message, "success");
        fetchExpenses();
        setModalOpen(false);
      } else {
        Swal.fire("Error", res.data.message, "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setFormData({
      id: row.id,
      category_id: row.category_id,
      amount: row.amount,
      date: row.date.includes("/")
        ? row.date.split("/").reverse().join("-")
        : row.date,
      staffs: row.staffs,
    });
    setMode("edit");
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios
          .post(`${globalValue}/expense_delete`, { id })
          .then((res) => {
            if (res.data.success === 1) {
              Swal.fire("Deleted!", res.data.message, "success");
              fetchExpenses();
            } else {
              Swal.fire("Error", res.data.message, "error");
            }
          })
          .catch((err) => Swal.fire("Error", err.message, "error"))
          .finally(() => setLoading(false));
      }
    });
  };

  const [pdf_data, setPdfData] = useState(null);
  const [pdfpopup, setPdfpopup] = useState(false);

  const openPdfModal = (row) => {
    setPdfData(row);
    setPdfpopup(true);
  };

  const handlePrint = () => {
    const content = document.getElementById("pdf-content");
    if (!content) return;

    html2canvas(content, { scale: 2, useCORS: true }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);

      let remainingHeight = pdfHeight - pdf.internal.pageSize.getHeight();
      while (remainingHeight > 0) {
        position -= pdf.internal.pageSize.getHeight();
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        remainingHeight -= pdf.internal.pageSize.getHeight();
      }

      pdf.save(`Expense_${pdf_data?.id || "receipt"}.pdf`);
      setPdfpopup(false);
    });
  };

  const columns = [
    { name: "Id", selector: (row) => row.id, sortable: true },
    { name: "Category", selector: (row) => row.category_name, sortable: true },
    { name: "Amount", selector: (row) => row.amount, sortable: true },
    { name: "Expense name", selector: (row) => row.staffs, sortable: true },
    { name: "Date", selector: (row) => row.date, sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-4">
          <button
            onClick={() => handleEdit(row)}
            className="p-2 text-white border bg-blue-700 hover:bg-blue-800 rounded-md shadow-xl"
          >
            <i className="fa fa-edit"></i>
          </button>
          <button
            onClick={() => {
              handleDelete(row.id);
            }}
            className="p-2 text-white border bg-yellow-500 hover:bg-yellow-800 rounded-md shadow-xl"
          >
            <i className="fa fa-trash"></i>
          </button>
          <button
            onClick={() => openPdfModal(row)}
            className="p-2 text-white border bg-green-600 hover:bg-green-800 rounded-md shadow-xl"
            title="Print PDF"
          >
            <i className="fa fa-print"></i>
          </button>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        width: "100%",
        border:
          theme === "dark"
            ? "solid 1px rgb(63, 62, 62)"
            : "solid 1px rgb(226, 232, 240)",
      },
    },
    headCells: {
      style: {
        backgroundColor:
          theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
        color: theme === "dark" ? "white" : "black",
        fontWeight: "bold",
      },
    },
    cells: {
      style: {
        color: theme === "dark" ? "white" : "black",
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        border:
          theme === "dark"
            ? "solid 1px rgb(63, 62, 62)"
            : "solid 1px rgb(255, 255, 255)",
      },
    },
    pagination: {
      style: {
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        color: theme === "dark" ? "white" : "black",
        borderTop:
          theme === "dark" ? "solid 1px white" : "solid 1px rgb(226, 232, 240)",
      },
      pageButtonsStyle: {
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        color: theme === "dark" ? "white" : "black",
        fill: theme === "dark" ? "white" : "black",
        "&:hover": {
          backgroundColor:
            theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
        },
      },
    },
  };

  const [staffList, setStaffList] = useState([]);
  const [categoryExpenseNames, setCategoryExpenseNames] = useState([]);

  const fetchStaffList = async () => {
    console.log("FETCH CALL");
    const response = await axios.get(`${globalValue}/staffs`, {
      params: {
        data: sessionStorage.getItem("branch"),
        position: "",
      },
      headers: {
        "x-access-token": sessionStorage.getItem("token"),
      },
    });

    const formatted = response.data.map((s) => ({
      id: s[0],
      code: s[1],
      name: s[2],
      phone: s[3],
      address: s[4],
      username: s[5],
      roles: s[6],
      branch: s[7],
      email: s[8],
    }));
    console.log("GGG", formatted);
    setStaffList(formatted);
  };

  useEffect(() => {
    if (formData.category_id) {
      const categoryName = categories.find(
        (c) => c.id === Number(formData.category_id)
      )?.category_name;

      if (categoryName?.toLowerCase().includes("salary staff")) {
        console.log("IF");
        fetchStaffList();
        setCategoryExpenseNames([]);
      } else {
        console.log("ELSEE", filteredExpenses);
        const uniqueNames = [
          ...new Set(
            filteredExpenses
              .filter(
                (item) => item.category_id === Number(formData.category_id)
              )
              .map((item) => item.staffs)
          ),
        ];
        console.log("UUU", uniqueNames);
        setCategoryExpenseNames(uniqueNames);
      }
    }
  }, [formData.category_id, expenses, categories]);

  useEffect(() => {
    if (formData.category_id) {
      const categoryName = categories.find((c) => c.id === formData.category_id)
        ?.category_name;
      if (categoryName?.toLowerCase().includes("salary staff")) {
        fetchStaffList();
      }
    }
  }, [formData.category_id]);

  return (
    <div>
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
          <img src={engineGif} alt="Loading..." className="w-24 h-24" />
        </div>
      )}

      <div
        className={`md:ml-[250px] w-full md:w-[calc(100%-250px)] mt-16 h-[92vh] flex justify-center items-center box-border ${
          theme === "dark"
            ? "bg-gray-900 text-white"
            : "bg-gray-100 text-gray-800"
        }`}
      >
        <div
          className={`w-[95%] md:w-[98%] h-[95%] px-4 py-2 rounded-xl shadow-xl overflow-auto ${
            theme === "dark"
              ? "bg-gray-800 text-gray-200"
              : "bg-white text-gray-800"
          }`}
        >
          <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
            <Head_data
              data="Manage Expenses"
              font=""
              size="3xl"
              color={theme === "dark" ? "text-gray-200" : "text-gray-800"}
            />
            <div className="flex w-full md:w-auto gap-2">
              <button
                className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700"
                onClick={() => {
                  setFormData({
                    id: null,
                    category_id: "",
                    amount: "",
                    date: getTodayDate(), // Set today's date for new expense
                    staffs: "",
                  });
                  setMode("add");
                  setModalOpen(true);
                }}
              >
                Add Expense
              </button>
            </div>
          </div>
          <input
            type="text"
            placeholder="Search by category or expense name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-64 px-4 my-2 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
          />

          <DataTable
            columns={columns}
            data={filteredExpenses}
            pagination
            striped
            highlightOnHover
            customStyles={customStyles}
          />

          {/* Modal */}
          <Modal show={modalOpen} onClose={() => setModalOpen(false)}>
            <Modal.Header>
              {mode === "add" ? "Add Expense" : "Edit Expense"}
            </Modal.Header>
            <Modal.Body>
              <div className="space-y-4">
                <div>
                  <Label>Category</Label>
                  <Select
                    value={formData.category_id}
                    onChange={(e) =>
                      setFormData({ ...formData, category_id: e.target.value })
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.category_name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label>Expense name</Label>
                  {(() => {
                    const categoryName = categories.find(
                      (c) => c.id === Number(formData.category_id)
                    )?.category_name;
                    if (categoryName?.toLowerCase().includes("salary staff")) {
                      return (
                        <Select
                          value={formData.staffs}
                          onChange={(e) =>
                            setFormData({ ...formData, staffs: e.target.value })
                          }
                        >
                          <option value="">Select Staff</option>
                          {staffList.map((staff) => (
                            <option key={staff.id} value={staff.name}>
                              {staff.name}
                            </option>
                          ))}
                        </Select>
                      );
                    } else {
                      return (
                        <div>
                          <input
                            list="expenseSuggestions"
                            type="text"
                            value={formData.staffs}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                staffs: e.target.value,
                              })
                            }
                            className="w-full p-2 border rounded-md"
                          />
                          <datalist id="expenseSuggestions">
                            {categoryExpenseNames.map((name, idx) => (
                              <option key={idx} value={name} />
                            ))}
                          </datalist>
                        </div>
                      );
                    }
                  })()}
                </div>

                <div>
                  <Label>Amount</Label>
                  <TextInput
                    type="number"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                  />
                </div>
               
                <div>
                  <Label>Date</Label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="w-full p-2 border rounded-md"
                    disabled={sessionStorage.getItem("position") !== "superadmin"}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button_submit
                onClick={handleSubmit}
                data={`${mode === "add" ? "Submit" : "Update"}`}
                type="submit"
                className="ms-auto"
              >
                {mode === "add" ? "Submit" : "Update"}
              </Button_submit>
            </Modal.Footer>
          </Modal>

          <Modal
            show={pdfpopup}
            size="6xl"
            popup={true}
            onClose={() => setPdfpopup(false)}
          >
            <Modal.Header className="bg-white text-gray-900 border-b border-gray-200"></Modal.Header>
            <Modal.Body className="flowbite-modal-b">
              <div
                id="pdf-content"
                className="border border-gray-500 mx-2 bg-white p-6 text-gray-800"
                style={{ minWidth: "700px" }}
              >
                <div className="flex items-center mb-4">
                  <div className="">
                    <img
                      src={Logo}
                      alt="JALS Automobiles Logo"
                      className="h-16 w-auto"
                    />
                  </div>

                  <div className="flex-1 text-center">
                <h2 className="text-2xl font-bold uppercase">
                  JALS AUTOMOBILES
                </h2>

                {sessionStorage.getItem("branch") === "JBRANCH2025030" ? (
                  <>
                    <p>
                      Door No : 6/63, Jals Road, Kuppottu Vilai,
                      <br />
                      Kadamacode, Melpuram, Pacode Post - 629168
                    </p>
                    <p>Ph : 04651-264007 | Mob : 8300451644</p>
                    <p>GSTIN : 33AMEPL9265E1Z1</p>
                  </>
                ) : (
                  <>
                    <p>
                      6/31 F, Karavilagam Road, Chennithottam Junction, Vettuvanni,
                      <br />
                      Marthandam - 629162
                    </p>
                    <p>Ph: 04651-271220 | Mob: 9488495820</p>
                    <p>GSTIN: 33AMEPL9265E1Z1</p>
                  </>
                )}
              </div>

                </div>

                <div className="flex justify-between items-center border-b border-gray-500 pb-2">
                  <h3 className="text-lg font-bold">EXPENSE RECEIPT</h3>
                  <div className="text-right">
                    <p>
                      Date:{" "}
                      <span className="font-semibold">
                        {pdf_data ? pdf_data.date : ""}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="border border-gray-400 p-4 mt-4 mb-4">
                  <p>
                    <strong>To:</strong>
                  </p>
                  <p>Address: </p>
                  <p>Vehicle No: </p>
                  <p>Phone: </p>
                </div>

                <div className="overflow-x-auto rounded-xl shadow-md border border-gray-200 mb-4">
                  <table className="min-w-full divide-y divide-gray-200 text-sm text-gray-800">
                    <thead className="bg-gray-100 text-left">
                      <tr>
                        <th className="px-6 py-3 font-semibold">Category</th>
                        <th className="px-6 py-3 font-semibold">
                          Expense Name
                        </th>
                        <th className="px-6 py-3 font-semibold">Amount</th>
                        <th className="px-6 py-3 font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      <tr>
                        {console.log("LLL", pdf_data)}
                        <td className="px-6 py-4">
                          {pdf_data?.category_name || "—"}
                        </td>
                        <td className="px-6 py-4">{pdf_data?.staffs || "—"}</td>
                        <td className="px-6 py-4 text-green-600 font-medium">
                          Rs. {pdf_data?.amount || "—"}
                        </td>
                        <td className="px-6 py-4">
                          {pdf_data ? pdf_data.date : ""}{" "}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 flex justify-between">
                  <div>
                    <p className="font-bold">For JALS AUTOMOBILES</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">Authorized Signatory</p>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="flowbite-modal-b">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Print
              </button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default Expenses;
